import React, { useCallback, useMemo, useReducer } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { useMomentStory } from '@/contexts/MomentStoryContext'
import { MomentStoryModal } from '@/organisms/CollectibleSceneInfo/MomentStoryModal/MomentStoryModal'
import { EngagementDetailUser } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext/CollectiblesPlayerEngagementContext'
import { PlayableStory } from '@/utils/StoryUtil/StoryUtil'

interface MomentStoryModalState {
  isOpen: boolean
  onOpen: (story: PlayableStory) => void
  onClose: () => void
}

const noop = () => undefined

const MomentStoryModalContext = React.createContext<MomentStoryModalState>({
  isOpen: false,
  onOpen: noop,
  onClose: noop,
})

interface MomentStoryModalReducerState {
  isOpen: boolean
  shouldClearStoryOnClose: boolean
}

type MomentStoryModalReducerAction =
  | { type: 'OPEN_MODAL'; payload: { shouldClearStoryOnClose: boolean } }
  | { type: 'CLOSE_MODAL' }

const reducer = (
  state: MomentStoryModalReducerState,
  action: MomentStoryModalReducerAction,
): MomentStoryModalReducerState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...action?.payload,
        isOpen: true,
      }
    case 'CLOSE_MODAL':
      return {
        isOpen: false,
        shouldClearStoryOnClose: false,
      }
    default:
      return state
  }
}

interface MomentStoryModalProviderProps {
  children: React.ReactNode
  story?: PlayableStory
  user?: Maybe<EngagementDetailUser>
}

export const MomentStoryModalProvider: React.FC<MomentStoryModalProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    isOpen: false,
    shouldClearStoryOnClose: false,
  })

  const { story, selectUserStory, clearSelectedUserStory } = useMomentStory()

  const onClose = useCallback(() => {
    dispatch({ type: 'CLOSE_MODAL' })

    if (state.shouldClearStoryOnClose) {
      clearSelectedUserStory()
    }

    const html = window.document.getElementsByTagName('html')[0]
    if (html) {
      html.style.height = 'auto'
      html.style.overflow = 'auto'
    }
  }, [clearSelectedUserStory, state.shouldClearStoryOnClose])

  const onOpen = useCallback(
    (storyToOpen: PlayableStory) => {
      const storyToOpenNotSameAsMomentStoryState = storyToOpen?.id !== story?.id
      dispatch({ type: 'OPEN_MODAL', payload: { shouldClearStoryOnClose: storyToOpenNotSameAsMomentStoryState } })

      if (storyToOpenNotSameAsMomentStoryState && storyToOpen) {
        selectUserStory(storyToOpen)
      }

      const html = window.document.getElementsByTagName('html')[0]
      if (html) {
        html.style.height = '100vh'
        html.style.overflow = 'hidden'
      }
    },
    [selectUserStory, story?.id],
  )

  const value = useMemo(() => {
    return {
      onOpen,
      onClose,
      isOpen: state.isOpen,
    }
  }, [onOpen, onClose, state.isOpen])

  return (
    <MomentStoryModalContext.Provider value={value}>
      {children}
      {state.isOpen && <MomentStoryModal onClose={onClose} />}
    </MomentStoryModalContext.Provider>
  )
}

export const useMomentStoryModal = () => {
  return React.useContext(MomentStoryModalContext)
}
